import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import { countWaiting, listJobs } from '../lib/device-db';
import { useDeviceQuery } from '../lib/hooks';
import { openJob } from '../lib/jobs';
import { recentJobs, type RecentJobRow } from '../lib/recent-jobs';
import { useRecentJobsFromCrm } from '../lib/use-recent-jobs';
import { ErrorMessage } from './message';

function JobRow({ job, busy, onOpen }: { job: RecentJobRow; busy: boolean; onOpen: () => void }) {
  return (
    <li>
      <button type="button" onClick={onOpen} disabled={busy} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 disabled:opacity-60">
        <span className="min-w-0 flex-1 sm:flex sm:items-baseline sm:gap-3">
          <span className="block font-medium text-slate-900 sm:shrink-0">{job.number}</span>
          <span className="block truncate text-sm text-slate-600">{job.name ?? 'No job name in CRM'}</span>
        </span>
        <span className="flex shrink-0 items-center gap-2 text-xs">
          {job.waiting > 0 && <span className="font-medium text-amber-700">{job.waiting} not sent yet</span>}
          {busy ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
        </span>
      </button>
    </li>
  );
}

/**
 * The jobs the user opened lately, on any device, newest first (#422). A job
 * on this device opens straight away; one that isn't is copied here first,
 * which needs signal (openJob says so when there's none).
 */
export function RecentJobs() {
  const navigate = useNavigate();
  const fromCrm = useRecentJobsFromCrm();
  const onDevice = useDeviceQuery(async () => Promise.all((await listJobs()).map(async (job) => ({ job, waiting: await countWaiting(job.id) }))), []);
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!onDevice) return null;
  const jobs = recentJobs(fromCrm, onDevice);
  if (!jobs.length) return null;

  async function open(job: RecentJobRow) {
    setError(null);
    if (job.onDevice) return navigate(`/job/${job.id}`);
    setOpening(job.id);
    try {
      navigate(`/job/${await openJob(job.number)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setOpening(null);
    }
  }

  return (
    <section className="mt-6">
      <h2 className="mb-2 text-sm font-semibold text-slate-700">Recent jobs</h2>
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
        {jobs.map((job) => (
          <JobRow key={job.id} job={job} busy={opening === job.id} onOpen={() => void open(job)} />
        ))}
      </ul>
      {error && <ErrorMessage className="mt-2">{error}</ErrorMessage>}
    </section>
  );
}
